import {
  type ChartData,
  type ChartOptions,
  type ScriptableTooltipContext,
} from "chart.js";

import {
  KpiType,
  type Kpi,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { chartTooltip } from "./weekly-accuracy-chart-tooltip";

const commonWkAnnotationOptions = {
  type: "label",
  display: true,
  content: "WK-4",
  xMin: 0,
  xMax: 0,
  yMin: 0,
  yMax: 0,
  yAdjust: 50,
  textAlign: "right" as const,
  font: { size: 12, family: "Commons Pro", weight: "bold" },
  backgroundColor: "transparent",
  color: "#021330",
} as const;

export const getChartOptions = (
  user: User,
  kpi: Kpi,
  data: ChartData<any, { x: Date; y: number }[]>,
) => {
  const isRevenue = kpi.kpi_type === KpiType.REVENUE;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: (ctx: ScriptableTooltipContext<"line">) =>
          chartTooltip(ctx, kpi.kpi_type),
        isRevenue,
        user,
        tooltipTitle: kpi.friendly_name,
      },
      legend: { display: false },
      annotation: {
        clip: false,
        common: { drawTime: "beforeDraw" },
        annotations: data.labels?.map((_, idx) => ({
          ...commonWkAnnotationOptions,
          content: `WK-${idx + 1}`,
          xMin: idx,
          xMax: idx,
        })),
      },
    },
    layout: {
      padding: {
        top: 28,
      },
    },
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        grace: "50%",
        beginAtZero: true,
        min: 0,
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 12,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val: string | number) => {
            if (isRevenue) {
              return MetricFormatters.currency(
                Number(val),
                user.currencies.currentCurrency,
                {
                  precision: "approximate",
                },
              );
            } else {
              return MetricFormatters.decimal(
                Number(val),
                user.currencies.currentCurrency,
                {
                  precision: "approximate",
                },
              );
            }
          },
        },
      },
      x: {
        axis: "x",
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          padding: 20,
          maxRotation: 0,
          autoSkip: true,
          font: {
            family: "Commons Pro",
            size: 13,
          },
        },
      },
    },
  } as ChartOptions<"line">;
};
